import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import productService from '../../../services/productService';

const AttributeForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    type: 'select',
    unit: '',
    isRequired: false,
    sortOrder: 0,
  });

  const attributeTypes = [
    { value: 'select', label: t('attributes_ext.types.select') },
    { value: 'color', label: t('attributes_ext.types.color') },
    { value: 'text', label: t('attributes_ext.types.text') },
    { value: 'number', label: t('attributes_ext.types.number') },
  ];

  useEffect(() => {
    if (isEdit) {
      fetchAttribute();
    }
  }, [id, isEdit]);

  const fetchAttribute = async () => {
    try {
      const data = await productService.getAttributeById(id);
      setFormData({
        name: data.name,
        type: data.type,
        unit: data.unit || '',
        isRequired: data.isRequired,
        sortOrder: data.sortOrder || 0,
      });
    } catch (error) {
      toast.error(t('attributes.fetchError'));
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const attributeData = {
        ...formData,
        sortOrder: parseInt(formData.sortOrder) || 0,
      };

      if (isEdit) {
        await productService.updateAttribute(id, attributeData);
        toast.success(t('attributes.updateSuccess'));
      } else {
        await productService.createAttribute(attributeData);
        toast.success(t('attributes.createSuccess'));
      }
      navigate('/attributes');
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.saveError'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {isEdit ? t('attributes.editAttribute') : t('attributes.createAttribute')}
      </Typography>

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Attribute Name */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('attributes_ext.attributeName')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder={t('attributes_ext.namePlaceholder')}
                />
              </Grid>

              {/* Attribute Type */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label={t('attributes_ext.attributeType')}
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                >
                  {attributeTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Unit */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('attributes_ext.unitOptional')}
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  placeholder={t('attributes_ext.unitPlaceholder')}
                  helperText={t('attributes_ext.unitHelper')}
                />
              </Grid>

              {/* Sort Order */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('common.sortOrder')}
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({ ...formData, sortOrder: e.target.value })}
                  helperText={t('common.sortOrderHelper')}
                />
              </Grid>

              {/* Required */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isRequired}
                      onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                    />
                  }
                  label={t('attributes_ext.required')}
                />
              </Grid>

              {/* Form Actions */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button onClick={() => navigate('/attributes')} disabled={loading}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" variant="contained" disabled={loading}>
                    {loading ? t('common.save') + '...' : isEdit ? t('attributes.editAttribute') : t('attributes.createAttribute')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AttributeForm;